import React, { useState } from 'react';
import { Calendar, Clock, Tag } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import AudioPlayer from './AudioPlayer';
import { episodes } from '../data/mockData';

const Episodes = () => {
  const [selectedEpisode, setSelectedEpisode] = useState(episodes[0]);
  const [showAll, setShowAll] = useState(false);

  const featuredEpisodes = episodes.filter(ep => ep.featured);
  const displayEpisodes = showAll ? episodes : featuredEpisodes;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <section id="episodes" className="py-24 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 right-20 w-96 h-96 bg-purple-500 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-20 left-20 w-72 h-72 bg-pink-500 rounded-full blur-3xl animate-pulse-fast"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-500 to-purple-500 mb-4 animate-gradient-fast">
            Latest Episodes
          </h2>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-1 w-24 bg-gradient-to-r from-transparent via-purple-500 to-transparent"></div>
            <div className="h-2 w-2 bg-purple-500 rounded-full animate-pulse"></div>
            <div className="h-1 w-24 bg-gradient-to-r from-transparent via-purple-500 to-transparent"></div>
          </div>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto font-medium">
            Dive into conversations about reggae, culture, and the entertainment industry
          </p>
        </div>

        {/* Featured Player */}
        <div className="mb-16">
          <AudioPlayer episode={selectedEpisode} />
        </div>

        {/* Episode Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {displayEpisodes.map((episode) => (
            <div
              key={episode.id}
              className={`group bg-gradient-to-br from-gray-900 to-black border rounded-xl p-6 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-2xl ${
                selectedEpisode.id === episode.id
                  ? 'border-purple-500 shadow-lg shadow-purple-500/40 bg-gradient-to-br from-purple-900/20 to-black'
                  : 'border-gray-800 hover:border-purple-500/50 hover:shadow-purple-500/20'
              }`}
              onClick={() => setSelectedEpisode(episode)}
            >
              {/* Episode Number Badge */}
              <div className="flex justify-between items-start mb-4">
                <Badge className="bg-purple-600/30 text-purple-300 border-purple-500/50 font-bold">
                  S{episode.season}:E{episode.episode}
                </Badge>
                <div className="flex items-center text-gray-400 text-sm font-semibold">
                  <Clock className="h-4 w-4 mr-1 text-purple-500" />
                  {episode.duration}
                </div>
              </div>

              {/* Title & Description */}
              <h3 className="text-xl font-bold text-white mb-3 group-hover:text-purple-400 transition-colors">
                {episode.title}
              </h3>
              <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                {episode.description}
              </p>

              {/* Date */}
              <div className="flex items-center text-gray-500 text-sm mb-4">
                <Calendar className="h-4 w-4 mr-2 text-purple-400" />
                {formatDate(episode.date)}
              </div>

              {/* Topics */}
              <div className="flex flex-wrap gap-2">
                {episode.topics.map((topic, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-800 text-gray-300 border border-gray-700 group-hover:border-purple-500/30 transition-colors"
                  >
                    <Tag className="h-3 w-3 mr-1 text-purple-500" />
                    {topic}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Show More Button */}
        {!showAll && episodes.length > featuredEpisodes.length && (
          <div className="text-center">
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-purple-500 text-purple-300 hover:bg-purple-500/20 px-8 py-6 transition-all duration-300 hover:border-purple-400 hover:scale-105 font-bold"
              onClick={() => setShowAll(true)}
            >
              View All Episodes (0)
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Episodes;
